<!-- Polaris (fase 5.1, D-C1): the contacts list as the kit's table (areas/contacts/PeopleList.vue), with the
     woot's data and behavior. From md up a `ui/table` with a checkbox column and "select all" (R2-5-PC-12 = A);
     columns leave by the width of the list, not of the window (a container query on `@container/list`, set by
     ContactsListLayout), City/Country and Created first. Below md, the compact items (R2-5-PC-14 = B). -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ArrowDown16,
  ArrowUp16,
  ArrowsVertical16,
  Checkmark16,
  Subtract16,
} from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import {
  Table,
  TableBody,
  TableCaption,
  TableHead,
  TableHeader,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  caption: { type: String, default: '' },
  openContactId: { type: [Number, String], default: null },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['toggleContact', 'toggleAll', 'sort', 'showContact']);

const { t } = useI18n();

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));
const isSelected = id => selectedIdsSet.value.has(id);
const isOpen = id =>
  props.openContactId !== null && String(props.openContactId) === String(id);

const allChecked = computed(() => {
  const picked = props.contacts.filter(c => isSelected(c.id)).length;
  if (!picked) return false;
  return picked === props.contacts.length ? true : 'indeterminate';
});

// R2-5-PC-7 = C: the columns with a field on the server sort, in the same state as the Sort menu
// (`contacts_sort_by`, sorted by the server, never in the browser). City / Country reads both of the menu's fields.
const SORTS = {
  NAME: ['name'],
  COMPANY: ['company_name'],
  LOCATION: ['city', 'country'],
  LAST_ACTIVITY: ['last_activity_at'],
  CREATED: ['created_at'],
};
const sortOf = column =>
  SORTS[column].includes(props.activeSort)
    ? { '': 'ascending', '-': 'descending' }[props.activeOrdering]
    : 'none';
const sortBy = column => {
  const field = SORTS[column].includes(props.activeSort)
    ? props.activeSort
    : SORTS[column][0];
  const flip = sortOf(column) === 'ascending' ? '-' : '';
  emit('sort', { sort: field, order: flip });
};

// The page opens the contact (the drawer and its route, fase 5.3).
const onClickViewDetails = id => emit('showContact', id);

const handleSelect = (id, value) => {
  emit('toggleContact', { id, value });
};
</script>

<template>
  <!-- The table's own container does not scroll: the list column does, so the header row sticks to it. -->
  <div class="[&_[data-slot=table-container]]:overflow-visible">
    <Table class="hidden md:table" data-contacts-table>
      <TableCaption class="sr-only">{{ caption }}</TableCaption>
      <TableHeader class="sticky top-0 z-10 bg-polaris-background">
        <TableRow class="hover:bg-transparent">
          <TableHead class="w-10 ps-4">
            <label class="flex size-8 items-center justify-center">
              <Checkbox
                v-slot="{ state }"
                :model-value="allChecked"
                :aria-label="t('CONTACTS_POLARIS.LIST.SELECT_ALL')"
                @update:model-value="value => emit('toggleAll', value === true)"
              >
                <Subtract16 v-if="state === 'indeterminate'" class="size-3.5" />
                <Checkmark16 v-else class="size-3.5" />
              </Checkbox>
            </label>
          </TableHead>
          <TableHead :aria-sort="sortOf('NAME')">
            <Button
              variant="ghost"
              size="sm"
              class="-mx-2.5"
              data-sort-column="NAME"
              @click="sortBy('NAME')"
            >
              {{ t('CONTACTS_POLARIS.LIST.COLUMNS.NAME') }}
              <ArrowUp16
                v-if="sortOf('NAME') === 'ascending'"
                aria-hidden="true"
              />
              <ArrowDown16
                v-else-if="sortOf('NAME') === 'descending'"
                aria-hidden="true"
              />
              <ArrowsVertical16
                v-else
                class="text-polaris-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </TableHead>
          <TableHead class="hidden @[38rem]/list:table-cell">
            {{ t('CONTACTS_POLARIS.LIST.COLUMNS.PHONE') }}
          </TableHead>
          <TableHead
            class="hidden @[49rem]/list:table-cell"
            :aria-sort="sortOf('COMPANY')"
          >
            <Button
              variant="ghost"
              size="sm"
              class="-mx-2.5"
              data-sort-column="COMPANY"
              @click="sortBy('COMPANY')"
            >
              {{ t('CONTACTS_POLARIS.LIST.COLUMNS.COMPANY') }}
              <ArrowUp16
                v-if="sortOf('COMPANY') === 'ascending'"
                aria-hidden="true"
              />
              <ArrowDown16
                v-else-if="sortOf('COMPANY') === 'descending'"
                aria-hidden="true"
              />
              <ArrowsVertical16
                v-else
                class="text-polaris-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </TableHead>
          <TableHead
            class="hidden @[60rem]/list:table-cell"
            :aria-sort="sortOf('LOCATION')"
          >
            <Button
              variant="ghost"
              size="sm"
              class="-mx-2.5"
              data-sort-column="LOCATION"
              @click="sortBy('LOCATION')"
            >
              {{ t('CONTACTS_POLARIS.LIST.COLUMNS.LOCATION') }}
              <ArrowUp16
                v-if="sortOf('LOCATION') === 'ascending'"
                aria-hidden="true"
              />
              <ArrowDown16
                v-else-if="sortOf('LOCATION') === 'descending'"
                aria-hidden="true"
              />
              <ArrowsVertical16
                v-else
                class="text-polaris-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </TableHead>
          <TableHead
            class="hidden @lg/list:table-cell"
            :aria-sort="sortOf('LAST_ACTIVITY')"
          >
            <Button
              variant="ghost"
              size="sm"
              class="-mx-2.5"
              data-sort-column="LAST_ACTIVITY"
              @click="sortBy('LAST_ACTIVITY')"
            >
              {{ t('CONTACTS_POLARIS.LIST.COLUMNS.LAST_ACTIVITY') }}
              <ArrowUp16
                v-if="sortOf('LAST_ACTIVITY') === 'ascending'"
                aria-hidden="true"
              />
              <ArrowDown16
                v-else-if="sortOf('LAST_ACTIVITY') === 'descending'"
                aria-hidden="true"
              />
              <ArrowsVertical16
                v-else
                class="text-polaris-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </TableHead>
          <TableHead
            class="hidden pe-4 @[70rem]/list:table-cell"
            :aria-sort="sortOf('CREATED')"
          >
            <Button
              variant="ghost"
              size="sm"
              class="-mx-2.5"
              data-sort-column="CREATED"
              @click="sortBy('CREATED')"
            >
              {{ t('CONTACTS_POLARIS.LIST.COLUMNS.CREATED') }}
              <ArrowUp16
                v-if="sortOf('CREATED') === 'ascending'"
                aria-hidden="true"
              />
              <ArrowDown16
                v-else-if="sortOf('CREATED') === 'descending'"
                aria-hidden="true"
              />
              <ArrowsVertical16
                v-else
                class="text-polaris-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <ContactsCard
          v-for="contact in contacts"
          :id="contact.id"
          :key="contact.id"
          :name="contact.name"
          :email="contact.email"
          :thumbnail="contact.thumbnail"
          :phone-number="contact.phoneNumber"
          :additional-attributes="contact.additionalAttributes"
          :availability-status="contact.availabilityStatus"
          :last-activity-at="contact.lastActivityAt"
          :created-at="contact.createdAt"
          :is-selected="isSelected(contact.id)"
          :is-open="isOpen(contact.id)"
          @show-contact="onClickViewDetails"
          @select="value => handleSelect(contact.id, value)"
        />
      </TableBody>
    </Table>
    <ul
      class="m-0 list-none border-0 border-t border-solid border-polaris-border p-0 md:hidden"
      :aria-label="caption"
      data-contacts-items
    >
      <ContactsCard
        v-for="contact in contacts"
        :id="contact.id"
        :key="contact.id"
        layout="item"
        :name="contact.name"
        :email="contact.email"
        :thumbnail="contact.thumbnail"
        :phone-number="contact.phoneNumber"
        :additional-attributes="contact.additionalAttributes"
        :availability-status="contact.availabilityStatus"
        :last-activity-at="contact.lastActivityAt"
        :created-at="contact.createdAt"
        :is-selected="isSelected(contact.id)"
        :is-open="isOpen(contact.id)"
        @show-contact="onClickViewDetails"
        @select="value => handleSelect(contact.id, value)"
      />
    </ul>
  </div>
</template>
